<template>
	<div v-if="data && lifeStageData" class="shopping-cnt">
		<h1 v-if="previewOnly" class="category-name">
			{{ $t('budgetingTitle') }}
		</h1>
		<h1 v-else class="category-name">{{ $t('shoppingTitle') }}</h1>
		<div class="body-text-2 available-balance">
			<div>
				{{
					JA_LOCALIZATION_IRL ? $t('debitAccount') + ':' : $t('checking') + ':'
				}}
				<span> {{ checkingAccount | currency }}</span>
			</div>
			<div
				v-if="profileData.simulation.isCreditCardEnable"
				class="available-credit-balance"
			>
				{{ ' | ' + $t('availableCredit') + ':' }}
				<span> {{ debts.creditCard.balance | currency }}</span>
			</div>
			<div
				v-if="
					!!lifeStageData.autoLoanAmount &&
						category.assetName === 'transportation' &&
						$store.getters.autoLoanBankingAndFinance.hasApplied
				"
				class="available-credit-balance"
			>
				{{ ' | ' + $t('availableAutoLoan') + ':' }}
				<span> {{ lifeStageData.autoLoanAmount | currency }}</span>
			</div>
			<div
				v-if="
					!!lifeStageData.autoLoanAmount &&
						category.assetName === 'transportation' &&
						$store.getters.autoLoan.hasApplied
				"
				class="available-credit-balance"
			>
				{{ ' | ' + $t('availableAutoLoanDealership') + ':' }}
				<span> {{ lifeStageData.autoLoanDealershipAmount | currency }}</span>
			</div>
			<div
				v-if="
					!!lifeStageData.mortgageLoanAmount &&
						category.assetName === 'housing' &&
						$store.getters.mortgageLoan.hasApplied
				"
				class="available-credit-balance"
			>
				{{ ' | ' + $t('availableMortgageLoan') + ':' }}
				<span> {{ lifeStageData.mortgageLoanAmount | currency }}</span>
			</div>
		</div>

		<h2 class="heading-3">{{ $t('shoppingSubtitle') }}</h2>
		<div
			v-if="
				(!!lifeStageData.autoLoanAmount &&
					category.assetName === 'transportation' &&
					$store.getters.autoLoan.hasApplied) ||
					(!!lifeStageData.autoLoanAmount &&
						category.assetName === 'transportation' &&
						$store.getters.autoLoanBankingAndFinance.hasApplied)
			"
			class="select-auto-loan-type"
		>
			<h3 class="heading-3">
				{{ $t('autoLoanPlaceholder') + ' ' + $t('interest') }}
			</h3>
			<ja-payment-types
				:options="loanPaymentOptions"
				disable-deselection
				default-selected-option
				@optionSelected="onInterestSelected"
			></ja-payment-types>
		</div>
		<div v-if="data.shoppingRule" class="body-text-1">
			{{ data.shoppingRule }}
		</div>
		<div v-if="showTabs" class="tabs-outer-wrapper">
			<div class="tabs-inner-wrapper">
				<ja-segmented-control
					v-model="selectedTab"
					:options="orderedTabs"
				></ja-segmented-control>
			</div>
		</div>
		<magic-grid
			:key="`selectedTab_${selectedTab}`"
			class="cards-container"
			:gap="16"
			:max-cols="2"
			:max-col-width="320"
			:animate="true"
		>
			<template v-for="subcategory in currentCategories">
				<ja-shopping-card
					:key="subcategory.expenseSubCategory_ID"
					class="card"
					:preview-only="previewOnly"
					:adults-count="adultsCount"
					:children-count="childrenCount"
					:subcategory="subcategory"
					:data="getSubcategoryFromCart(subcategory.expenseSubCategory_ID)"
					:loan-info="getLoanDescription(subcategory)"
					:purchased-quantities="
						getQuantities(subcategory.expenseSubCategory_ID)
					"
					:disabled="disabled"
					:select-card="
						isSubcategorySelected(subcategory.expenseSubCategory_ID)
					"
					@subcategorySelected="selectSubcategory"
					@subcategoryDeselected="deselectSubcategory"
				></ja-shopping-card>
			</template>
		</magic-grid>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import ProfileData from '@/mixins/ProfileData';
import ShoppingCard from '../ShoppingCard/ShoppingCard';
import SegmentedControl from '../ui/SegmentedControl';
import LifeStageData from '@/mixins/LifeStageData';
import PaymentTypes from './PaymentTypes';
import { defaultNames, loanApplicableCategories } from '@/helpers/constants';

export default {
	components: {
		jaShoppingCard: ShoppingCard,
		jaSegmentedControl: SegmentedControl,
		jaPaymentTypes: PaymentTypes
	},
	mixins: [ProfileData, LifeStageData],
	props: {
		category: {
			type: Object,
			default() {
				return;
			}
		},
		data: {
			type: Object,
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		previewOnly: {
			type: Boolean,
			default: false
		},
		preselectedData: {
			type: Array,
			default: () => []
		},
		cart: {
			type: Array,
			default: () => []
		},
		paymentMethods: {
			type: Array,
			default: () => []
		},
		area: {
			type: Array,
			default: () => []
		},
		discardAllPurchases: {
			type: Boolean,
			default: false
		},
		deselectedDataId: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			selectedTab: null,
			cartData: [],
			currentCategories: [],
			requiredCategories: [],
			loanDescriptionKeys: ['transportation', 'housing'],
			preselectedSubIds: [],
			JA_LOCALIZATION_IRL: this.$store.getters.isIrl,
			areaSetting: null,
			autoLoanInterest: null
		};
	},
	apollo: {
		areaSetting: {
			query: gql`
				query getVariablesByArea {
					areaSettings {
						area
						autoLoanInterest
						autoLoanDealershipInterest
					}
				}
			`,
			update(data) {
				if (this.area) {
					const dataAreaSetting = data.areaSettings.filter(
						a => a.area === this.area[0]
					);
					return dataAreaSetting[0];
				}
			}
		}
	},
	computed: {
		loanPaymentOptions() {
			const paymentMethods = [];
			if (this.areaSetting) {
				this.paymentMethods.forEach(obj => {
					if (
						this.$store.getters.autoLoanBankingAndFinance.hasApplied &&
						obj.defaultName === defaultNames.AUTO_LOAN
					) {
						paymentMethods.push({
							name: this.$t('autoLoanBankingAndFinance'),
							value: obj.upperPaymentMethod_ID,
							defaultName: obj.defaultName,
							accountEnabled: true,
							amount: this.lifeStageData.autoLoanAmount,
							interest: this.areaSetting.autoLoanInterest
								? this.areaSetting.autoLoanInterest
								: null
						});
					}
					if (
						this.$store.getters.autoLoan.hasApplied &&
						obj.defaultName === defaultNames.AUTO_LOAN_DEALERSHIP
					) {
						paymentMethods.push({
							name: this.$t('autoLoanDealership'),
							value: obj.upperPaymentMethod_ID,
							defaultName: obj.defaultName,
							amount: this.lifeStageData.autoLoanDealershipAmount,
							accountEnabled: true,
							interest: this.areaSetting.autoLoanDealershipInterest
								? this.areaSetting.autoLoanDealershipInterest
								: null
						});
					}
				});
			}
			return paymentMethods;
		},
		showTabs() {
			return this.orderedTabs && Object.keys(this.orderedTabs).length > 1;
		},
		orderedTabs() {
			let orderedTabs = null;
			let unorderedTabs = this.unorderedTabs;
			if (unorderedTabs) {
				orderedTabs = {};
				Object.keys(unorderedTabs)
					.sort()
					.forEach(function(key) {
						orderedTabs[key] = unorderedTabs[key];
					});
			}
			return orderedTabs;
		},
		unorderedTabs() {
			let unorderedTabs = null;
			if (
				this.data &&
				this.data.cmsExpenseCategoryTabs.length &&
				this.data.cmsExpenseSubCategories.length
			) {
				unorderedTabs = this.data.cmsExpenseSubCategories.reduce(
					(result, subCategory) => {
						if (
							subCategory.cmsExpenseCategoryTab &&
							subCategory.cmsExpenseCategoryTab.name &&
							!result[subCategory.cmsExpenseCategoryTab.name]
						) {
							result[subCategory.cmsExpenseCategoryTab.name] =
								subCategory.cmsExpenseCategoryTab.name;
						}
						return result;
					},
					{}
				);
			}
			return unorderedTabs;
		},
		budget() {
			if (this.data.studentExpenseCategoryBudgets) {
				return this.data.studentExpenseCategoryBudgets[0].budget;
			}
			return null;
		},
		debts() {
			return this.$store.getters.debts;
		},
		checkingAccount() {
			return this.netMonthlyIncome - this.lifeStageData.totalExpenses;
		}
	},
	watch: {
		data() {
			this.groupSubCategories();
		},
		selectedTab() {
			this.currentCategories = null;
			this.groupSubCategories();
		},
		preselectedData() {
			// No need to execute the function if preselected data is empty
			if (this.preselectedData.length) {
				this.selectCardsByData();
			}
		},
		discardAllPurchases(a) {
			if (a) {
				this.deselectedCartData();
			}
		},
		deselectedDataId(newId) {
			this.deselectSubcategory(newId);
			this.deselectDataById(newId.expenseSubCategory_ID);
		}
	},
	async mounted() {
		if (this.data) {
			if (this.data.cmsExpenseCategoryTabs.length) {
				// get first available tab's value based on calculcations made in computed properties
				if (this.orderedTabs) {
					this.selectedTab = this.orderedTabs[Object.keys(this.orderedTabs)[0]];
				}
			}
			this.groupSubCategories();
		}
	},
	methods: {
		deselectedCartData() {
			this.preselectedSubIds = [];
			this.cartData.map(x => this.deselectSubcategory(x));
		},
		deselectDataById(id) {
			this.preselectedSubIds = this.preselectedSubIds.filter(
				subId => subId !== id
			);
		},
		onInterestSelected(method) {
			this.autoLoanInterest = method[0];
			this.$emit('autoLoanInterest', method[0]);
			this.groupSubCategories();
		},
		selectCardsByData() {
			// Preselected Data is array of objects beacause of the Quantity.
			// We should remap it in order to get only the subcateogires' ids
			const preselectedDataMapped = this.preselectedData.map(
				i => i.expenseSubCategoryId
			);

			const preslectedItems = this.data.cmsExpenseSubCategories
				.filter(item =>
					preselectedDataMapped.includes(item.expenseSubCategory_ID)
				)
				.map(item => item.expenseSubCategory_ID);

			this.preselectedSubIds = preslectedItems;
		},
		getQuantities(subId) {
			const subcategory = this.preselectedData.find(
				i => i.expenseSubCategoryId === subId
			);

			let defaultQty = null;

			if (subcategory) {
				defaultQty = {
					adultQty: subcategory.adultQty,
					childQty: subcategory.childQty,
					selectionCount: Math.max(1, subcategory.adultQty)
				};
			}

			return defaultQty;
		},
		getLoanDescription(subcategory) {
			if (
				this.loanDescriptionKeys.includes(this.category.assetName) &&
				subcategory.showMontlyCost
			) {
				if (
					this.category.assetName.toLowerCase() ===
						loanApplicableCategories.TRANSPORTATION &&
					this.autoLoanInterest
				) {
					const isDealershipInterest =
						this.autoLoanInterest.defaultName ===
						defaultNames.AUTO_LOAN_DEALERSHIP;
					return this.$t(`loanDescription.${this.category.assetName}`, {
						monthlyLoanAmount: (isDealershipInterest
							? subcategory.monthlyLoanDealershipAmount
							: subcategory.monthlyLoanAmount ?? 0.0
						).toLocaleString('en-US'),
						additionalCost: (subcategory.additionalCost ?? 0.0).toLocaleString(
							'en-US'
						)
					});
				} else {
					return this.$t(`loanDescription.${this.category.assetName}`, {
						monthlyLoanAmount: (
							subcategory.monthlyLoanAmount ?? 0.0
						).toLocaleString('en-US'),
						additionalCost: (subcategory.additionalCost ?? 0.0).toLocaleString(
							'en-US'
						)
					});
				}
			}

			return null;
		},
		groupSubCategories() {
			if (this.data.cmsExpenseSubCategories) {
				let currentCategories = null;
				if (this.orderedTabs) {
					currentCategories = this.data.cmsExpenseSubCategories.map(
						subcategory => {
							const category = {
								...subcategory,
								isVisible:
									subcategory.cmsExpenseCategoryTab.name == this.selectedTab,
								isSelected: this.isSubcategorySelected(
									subcategory.expenseSubCategory_ID
								)
							};

							if (this.autoLoanInterest) {
								category.interestAutoLoan = this.autoLoanInterest.defaultName;
							}

							return category;
						}
					);
				} else {
					currentCategories = this.data.cmsExpenseSubCategories.map(i => {
						// We do not have tabs, so we want cards to be always visible
						const category = {
							...i,
							isVisible: true
						};

						if (this.autoLoanInterest) {
							category.interestAutoLoan = this.autoLoanInterest.defaultName;
						}

						return category;
					});
				}

				// disable item's mandatory if category is not mandatory
				/* if (!this.category.isMandatory) {
					currentCategories.forEach(subCategory => {
						subCategory.isMandatory = false;
					});
				} */

				this.currentCategories = currentCategories;
			}
		},
		getSubcategoryFromCart(subcategoryID) {
			for (let i = 0, len = this.cartData.length; i < len; i++) {
				if (this.cartData[i].expenseSubCategory_ID == subcategoryID) {
					return this.cartData[i];
				}
			}
			return null;
		},
		selectSubcategory(subcategory) {
			const selectedSubCategoryIndex = this.cartData.findIndex(
				x => x.expenseSubCategory_ID === subcategory.expenseSubCategory_ID
			);

			if (selectedSubCategoryIndex !== -1) {
				this.cartData[selectedSubCategoryIndex] = subcategory;
			} else {
				this.cartData.push(subcategory);
			}
			this.$announcer.polite(
				this.$t('shoppingCardSelected', { subcategory: subcategory.name })
			);
			this.updateCart();
		},
		deselectSubcategory(subcategory) {
			this.cartData = this.cartData.filter(x => {
				return (
					x.expenseSubCategory_ID !== subcategory.expenseSubCategory_ID &&
					x.expenseSubCategory_ID !== subcategory.expenseSubCategoryId
				);
			});

			this.$announcer.polite(
				this.$t('shoppingCardUnselected', { subcategory: subcategory.name })
			);

			const mainCategoryIndex = this.currentCategories.findIndex(
				category =>
					category.expenseSubCategory_ID ===
						subcategory.expenseSubCategory_ID ||
					category.expenseSubCategory_ID === subcategory.expenseSubCategoryId
			);
			if (mainCategoryIndex !== -1) {
				this.currentCategories[mainCategoryIndex].isSelected = false;
				if (
					this.currentCategories[mainCategoryIndex].subcategories &&
					this.currentCategories[mainCategoryIndex].subcategories.length > 0
				) {
					this.currentCategories[mainCategoryIndex].subcategories = [];
				}
			}
			this.updateCart();
		},
		updateCart() {
			this.$emit('cartUpdated', this.cartData);
		},
		// Used when a category is already purchased
		isSubcategorySelected(subId) {
			return this.preselectedSubIds.includes(subId);
		}
	}
};
</script>
<style lang="scss" scoped>
.shopping-cnt {
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;

	.category-name {
		margin-top: 0;
		margin-bottom: 16px;
	}

	h2 {
		margin-top: 0;
		margin-bottom: 32px;
	}
	h3 {
		margin: 10px;
	}

	.available-balance {
		display: flex;
		width: 90%;
		color: $font-dark;
		font-family: 'RobotoMedium';
		text-transform: uppercase;
		text-align: center;
		padding-bottom: 20px;
		border-bottom: 1px solid $container-border;
		justify-content: center;
		margin-bottom: 40px;
		margin-top: 10px;

		.available-credit-balance {
			margin-left: 4px;
		}
	}

	.body-text-1 {
		margin-bottom: 32px;
	}

	.tabs-outer-wrapper {
		width: 100%;
		text-align: center;
		margin-bottom: 32px;
		padding: 8px 0;
		border-top: 1px solid $container-border;
		border-bottom: 1px solid $container-border;

		.tabs-inner-wrapper {
			width: 90%;
			max-width: 1000px;
			margin: auto;
		}
	}

	.cards-container {
		width: 736px;

		.card {
			width: 320px !important;
			max-width: 320px !important;
		}
	}
	.select-auto-loan-type {
		display: flex;
		flex-direction: column;
		align-items: center;
		border: 2px solid $container-border;
		border-radius: 4px;
		padding: 0px 10px;
		margin-bottom: 10px;
	}
	::v-deep .select-list-container {
		display: flex;
		flex-direction: row;
		gap: 20px;
	}
}
</style>
